import styled from '@emotion/styled';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { L2Targets } from '@resistapp/common/assays';
import { friendlyL2Target } from '@resistapp/common/friendly';
import { AbundanceMode } from '@resistapp/common/types';
import { References } from '../../../hooks/use-science-reference';
import { friendlyGeneName } from '../../selected-map-sampling-point/site-details/site-details-utils';
import { oldTheme } from '../../shared/old-styles';
import { MetricTitle } from '../../shared/overview-metric-title';
import { ReductionIllustration } from './reduction-illustration';

export function ReductionInfo() {
  const { activeAbundanceMode, selectedAntibiotic } = useOverviewContext();
  const { queryFilters } = useSampleDataContext();
  const { getAssayInfo } = useAssayContext();
  const isVolumeMetric = activeAbundanceMode === AbundanceMode.COPIES_PER_L;

  // Determine the scope description based on what's selected
  const getScopeDescription = () => {
    // If specific genes are selected
    if (queryFilters.filters.selectedTargetGrouping === 'assay' && queryFilters.filters.selectedTargets.length > 0) {
      const geneNames = queryFilters.filters.selectedTargets
        .map(ay => friendlyGeneName(ay, getAssayInfo(ay)?.gene || ay))
        .join(', ');
      return `10-fold change shows the reduction for ${geneNames} gene${queryFilters.filters.selectedTargets.length > 1 ? 's' : ''}.`;
    }

    // If a specific antibiotic is selected
    if (selectedAntibiotic) {
      return `10-fold change considers all the measured ${friendlyL2Target(selectedAntibiotic as L2Targets)} resistance genes.`;
    }

    // Default case - all genes
    return '10-fold change considers all the measured genes.';
  };

  return (
    <div>
      <InfoSection>
        <div>
          <i>
            <MetricTitle noBold noColor />
          </i>{' '}
          {isVolumeMetric
            ? 'shows how many orders of magnitude the absolute abundance of effluent genes per liter changed compared to the influent samples.'
            : 'shows how many orders of magnitude the relative abundance of effluent genes changed compared to the influent samples.'}
        </div>
      </InfoSection>

      <InfoSection>
        <b>Scope of genes</b>
        <div style={{ marginTop: 4 }}>{getScopeDescription()}</div>
      </InfoSection>

      <InfoSection>
        <b>Metric calculation</b>
        <div style={{ marginTop: 4 }}>
          {isVolumeMetric ? (
            <>
              The metric is calculated by dividing the number of gene copies per liter of treated wastewater by the
              number of gene copies per liter of raw wastewater.
            </>
          ) : (
            <>
              First, a genewise fold change is calculated using the ΔCq method: First, the ΔCq is determined by
              subtracting the Cq value of 16S rRNA reference gene from the Cq value of the target gene in both the
              influent and effluent samples. Then, ΔΔCq is calculated by subtracting the ΔCq of the influent sample from
              the ΔCq of the effluent sample.
            </>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          {isVolumeMetric ? (
            <>
              To obtain an overall change across all genes and biological replicates, the geometric mean of the
              individual fold change ratios is calculated across genes and replicas.
            </>
          ) : (
            <>
              To obtain an overall change percentage across all genes and biological replicates, the geometric mean of
              the individual fold change ratios is calculated as 2<sup>−ΔΔCq</sup> (with ΔΔCq calculated across genes
              and replicas).
            </>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          Finally, this overall change ratio is presented as a logarithm of ten. A 10-fold change of 0 means no change
          in overall {isVolumeMetric ? 'absolute' : 'relative'} abundance of genes, a 10-fold change of -1 indicates a
          -90% change, a 10-fold change of 1 means a +900% change, and so on.
        </div>
      </InfoSection>

      <InfoSection>
        <b>Biological meaning</b>
        <div style={{ marginTop: 12 }}>
          The reduction performance of WWTP processes varies wildly for different genes. The mean reduction performance
          across Resistomap&apos;s OneHealth Panel in the European WWTPs is -2, but some antitiobics typically see -3
          reductions, while some individual genes are typically only reduced by -1.
        </div>

        <div style={{ marginTop: 12 }}>
          The following graph provides high level reference points of the reduction performance across all genes. When
          evaluating the performance for a specific antibiotic or gene, it&apos;s best to compare your performance
          against the European mean of the particular set of genes you are inspecting in the History performance graph.
        </div>
        <ReductionIllustration />
      </InfoSection>

      <InfoSection>
        <References style={{ marginTop: 25 }} />
      </InfoSection>
    </div>
  );
}

const InfoSection = styled.div`
  padding-bottom: ${oldTheme.spacing.xs};
`;
